/* Search Middleware implementation for Algolia SDK */
import {
  SearchClient as AlgoliaSearchClient,
  FetchedIndex,
  SearchResponse,
} from '@algolia/client-search';
import type { Hit } from '@algolia/client-search';
import { algoliasearch } from 'algoliasearch';

import { compact, forEach, groupBy, isNil } from 'lodash';

import {
  AlgoliaClientOptions,
  FacetsList,
  SearchClient,
  SearchFn,
  SearchIndexResult,
  SearchOptions,
} from 'lib/search';

const FILTER_OR_CONNECTOR = ' OR ';
const FILTER_AND_CONNECTOR = ' AND ';
const FILTER_ATTRIBUTES = {
  COMPANY_ID: 'company.companyId',
  READER_COMPANIES: 'readerCompanies',
};

const HITS_PER_PAGE_DEFAULT = 10;

function getBaseFilters<T extends SearchIndexResult>(
  searchOptions: SearchOptions<T>
) {
  // Algolia filters reference: https://www.algolia.com/doc/api-reference/api-parameters/filters/?client=javascript
  let filters: string[] = [];
  filters = compact([
    // All deleted records are excluded by default
    !searchOptions.includeDeleted && `deleted:false`,
  ]);
  // Check if search results should be limited to drivers only
  if (searchOptions.driversOnly) {
    filters.push(`operator.permissionGroups:"driver"`);
  }
  // Check if should filter by Operator's Company
  if (!!searchOptions.companiesFilter?.length) {
    const companyIdAttr = FILTER_ATTRIBUTES.COMPANY_ID;
    const companiesFilter = searchOptions.companiesFilter
      .map((companyId) => `${companyIdAttr}:"${companyId}"`)
      .join(FILTER_OR_CONNECTOR);
    filters.push(companiesFilter);
  }
  // Check if should filter by readerCompanies field
  if (!!searchOptions.readerCompaniesFilter) {
    const readerCompaniesAttr = FILTER_ATTRIBUTES.READER_COMPANIES;
    const readerCompaniesFilter = `${readerCompaniesAttr}:"${searchOptions.readerCompaniesFilter}"`;
    filters.push(readerCompaniesFilter);
  }
  return filters;
}

function getFiltersAndOptionalFilters<T extends SearchIndexResult>(
  searchOptions: SearchOptions<T>
) {
  // Check if should apply facet filters
  const filters = getBaseFilters(searchOptions);
  // Check if should apply optional facet filters
  // (see difference with regular filters: https://www.algolia.com/doc/api-reference/api-parameters/optionalFilters/)
  let optionalFilters: string[][] = [];

  if (!!searchOptions.searchFilters?.length) {
    const hasMandatoryAttributes = !!searchOptions.mandatoryAttributes;
    const facetsByAttribute = groupBy(searchOptions.searchFilters, (filter) => {
      return filter.split(':')[0];
    });
    const areAllFiltersMandatory = !hasMandatoryAttributes;
    forEach(facetsByAttribute, (facetFilters, facetAttribute) => {
      const isConjunctiveAttribute =
        searchOptions.conjunctiveAttributes?.includes(facetAttribute);
      if (
        areAllFiltersMandatory ||
        searchOptions.mandatoryAttributes?.includes(facetAttribute)
      ) {
        // Apply these facet filters as mandatory filters
        const filterLogicalConnector =
          // Check if should combine same-attribute filters as AND or OR clauses
          isConjunctiveAttribute ? FILTER_AND_CONNECTOR : FILTER_OR_CONNECTOR;
        const facetFiltersStr = facetFilters.join(filterLogicalConnector);
        filters.push(facetFiltersStr);
      } else {
        // Apply these facet filters as optional filters
        // NOTE: Optional filters have different syntax than regular filters
        const parsedFilters = facetFilters.map((filter) =>
          filter.replace(/"/g, '')
        );
        // Check how optional attribute filters should be logically combined.
        // https://support.algolia.com/hc/en-us/articles/11923043923217-How-can-I-configure-my-facet-attribute-as-conjunctive-AND-disjunctive-OR
        if (isConjunctiveAttribute) {
          // For optional conjunctive attributes append each filter individually
          optionalFilters = optionalFilters.concat(parsedFilters);
        } else {
          // For optional disjunctive attributes append the nested array of filters
          optionalFilters.push(parsedFilters);
        }
      }
    });
  }

  return {
    filters,
    optionalFilters,
  };
}

function getAlgoliaFilterStr(filters: string[]) {
  const algoliaFilterStr =
    filters.length <= 1
      ? filters[0]
      : // If more than 1 filter is applied then enclose them in parentheses
        filters.map((f) => `(${f})`).join(FILTER_AND_CONNECTOR);
  return algoliaFilterStr;
}

function parseSearchResultHit<T extends SearchIndexResult>(
  algoliaSearchResponse: SearchResponse<T>
) {
  return (hit: Hit<T>) => {
    // Add queryId to each result to be used when sending analytics events
    hit._queryId = algoliaSearchResponse.queryID;
    // Add extra values to rankingInfo
    hit._rankingInfo = Object.assign({}, hit._rankingInfo, {
      milesToCenter: (hit._rankingInfo?.geoDistance || 0) * 0.000621371,
      // Add total number of pages to each result
      page: algoliaSearchResponse.page,
      totalPages: algoliaSearchResponse.nbPages,
    });
    return hit;
  };
}

let lastQueryString = '';
let lastQueryParameters: Record<string, any> = {};

let knownIndices: FetchedIndex[] = [];

async function checkIfIndexExists(
  algoliaClient: AlgoliaSearchClient,
  indexName: string
) {
  let indices = knownIndices;
  // If the index name is not found in the known indices list,
  // fetch the list of indices from Algolia and update the known indices list
  if (!indices.length) {
    const algoliaIndices = await algoliaClient.listIndices();
    indices = algoliaIndices.items;
    knownIndices = indices;
  }
  return indices.some(({ name }) => name === indexName);
}

function getFallbackIndexName(indexName: string) {
  const indexWithClosestName = knownIndices.find(
    ({ name }) => indexName.startsWith(name) || name.startsWith(indexName)
  );
  return indexWithClosestName?.name || '';
}

// This record is shared across all different instances of AlgoliaClient,
// so each instance has to define its own clientId to use a key to save
// and access to its index name.
const indicesNames: Record<string, string> = {};

export default function AlgoliaClient<T extends SearchIndexResult>(
  options: AlgoliaClientOptions
): SearchClient<T> {
  const {
    algoliaAppId,
    algoliaApiKey,
    algoliaIndexName,
    sortByField,
    sortOrder,
  } = options;

  // Set an unique clientId based on parameters
  let clientId = [
    algoliaAppId,
    algoliaApiKey,
    algoliaIndexName,
    sortByField,
    sortOrder,
  ].join('-');

  const getFinalIndexName = () => indicesNames[clientId] || '';

  // Algolia client reference: https://www.algolia.com/doc/api-reference/api-methods/search/
  const client = algoliasearch(algoliaAppId, algoliaApiKey);
  let indexName = algoliaIndexName;
  // When search results should be sorted by a specific field and order a replica of the
  // original index will be used for searching.
  // NOTE: Before using these options the replica(s) should've been manually created and should follow specific naming conventions.
  // SEE: https://www.algolia.com/doc/guides/managing-results/refine-results/sorting/how-to/sort-by-attribute/
  if (!!sortByField && !!sortOrder) {
    // Ex: if sorting by field "timestamps.created" in ascending order,
    // the replica index should be named: "<indexName>_timestamps_created_asc"
    const indexSuffix = `_${sortByField.split('.').join('_')}_${sortOrder}`;
    indexName += indexSuffix;
  }

  const fakeSearchResponse = Promise.resolve([] as unknown as T[]);

  /**
   * Validates the Algolia index name and sets the final index name to be used for searches.
   * If the provided index name is not valid, it falls back to a known index with the closest name.
   *
   * @returns {Promise<void>} A promise that resolves once the index name is validated and the final index name is set.
   */
  const validateIndex = async () => {
    const isValidIndex = await checkIfIndexExists(client, indexName);
    if (isValidIndex) {
      indicesNames[clientId] = indexName;
      return isValidIndex;
    }
    const fallBackIndexName = getFallbackIndexName(indexName);
    console.warn(
      `WARN: Index "${indexName}" is not recognized as a valid index name`,
      `so all searches will be fallback to index "${fallBackIndexName}" instead.`,
      `Available indices are: ${knownIndices
        .map((index) => index.name)
        .sort()
        .join(', ')}`
    );
    indicesNames[clientId] = fallBackIndexName;
    return true;
  };

  const search: SearchFn<T> = async function (queryString, searchOptions = {}) {
    const isIndexValidated = await validateIndex();
    if (!isIndexValidated) {
      return fakeSearchResponse;
    }

    const { filters, optionalFilters } =
      getFiltersAndOptionalFilters(searchOptions);
    const algoliaFilterStr = getAlgoliaFilterStr(filters);

    const hitsPerPage = isNil(searchOptions.hitsPerPage)
      ? HITS_PER_PAGE_DEFAULT +
        // Increase page size to ensure there at least HITS_PER_PAGE_DEFAULT
        // results to shown without including excluded elements
        (searchOptions.excludeElements?.length || 0)
      : searchOptions.hitsPerPage;
    let algoliaSearchParameters: Record<string, any> = {
      // Include the ranking info for each search result
      getRankingInfo: true,
      // Include the queryId for each query
      clickAnalytics: true,
      filters: algoliaFilterStr,
      optionalFilters: optionalFilters,
      hitsPerPage: hitsPerPage,
      attributesToRetrieve: searchOptions.attributesToRetrieve,
      disableExactOnAttributes: searchOptions.disableExactOnAttributes,
      restrictSearchableAttributes: searchOptions.restrictSearchableAttributes,
    };

    // Check if should sort results by distance to a given set of coordinates.
    if (
      !!searchOptions.aroundCoordinates &&
      !!searchOptions.aroundCoordinates.lat &&
      !!searchOptions.aroundCoordinates.lng
    ) {
      const { lat, lng } = searchOptions.aroundCoordinates;
      algoliaSearchParameters.aroundLatLng = [lat, lng].join(',');
      // Do not filter by distance, only sort
      algoliaSearchParameters.aroundRadius = 'all';
    }

    const algoliaSearchResponse = await client.searchSingleIndex<T>({
      indexName: getFinalIndexName(),
      searchParams: {
        query: queryString,
        ...algoliaSearchParameters,
      },
    });

    const searchResults = algoliaSearchResponse.hits.map(
      parseSearchResultHit(algoliaSearchResponse)
    );

    lastQueryString = queryString;
    lastQueryParameters = algoliaSearchParameters;
    return searchResults;
  };

  const loadNextPage = async function (page: number) {
    const isIndexValidated = await validateIndex();
    if (!isIndexValidated) {
      return fakeSearchResponse;
    }
    const algoliaSearchResponse = await client.searchSingleIndex<T>({
      indexName: getFinalIndexName(),
      searchParams: {
        query: lastQueryString,
        ...{
          ...lastQueryParameters,
          page,
        },
      },
    });

    const searchResults = algoliaSearchResponse.hits.map(
      parseSearchResultHit(algoliaSearchResponse)
    );
    return searchResults;
  };

  const getAllFacets = async function (
    searchOptions: SearchOptions<T> = {},
    facetsQuery = ''
  ) {
    const isIndexValidated = await validateIndex();
    if (!isIndexValidated) {
      return [] as unknown as FacetsList;
    }
    const { filters } = getFiltersAndOptionalFilters(searchOptions);
    const algoliaFilterStr = getAlgoliaFilterStr(filters);
    // Run an empty query that fetches all facets
    const algoliaSearchResponse = await client.searchSingleIndex({
      indexName: getFinalIndexName(),
      searchParams: {
        query: facetsQuery,
        facets: ['*'],
        filters: algoliaFilterStr,
        hitsPerPage: 0,
      },
    });
    const facets = algoliaSearchResponse.facets;

    return facets as FacetsList;
  };

  return {
    validateIndex,
    search,
    loadNextPage,
    getAllFacets,
  };
}
